import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import { ROUTES } from '../../../../../presentation/routes/Paths';
import '../style/video-section.css';
import { getDocs, query, where } from 'firebase/firestore';
import { getClientCollection } from '../../../../../services/FirebaseService';
import { initializeVideoSectionPopup } from '../script/video-section';
import videoShape from '../../../../../assets/images/shapes/video-one-shape-1.png';
// Import image as fallback (Option B) - this ensures webpack processes it
import whyChooseUsImageSrc from '../../../../../assets/logo/why_choose_us/why_choose_us.webp';

// Use the public assets path (Option A) - this is what jarallax expects
const WHY_CHOOSE_US_IMAGE_PATH = '/assets/logo/why_choose_us/why_choose_us.webp';

const VideoSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const bgRef = useRef<HTMLDivElement>(null);
    const videoLinkRef = useRef<HTMLAnchorElement>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);

    // Access translations - recalculate when language changes to ensure re-render
    const translation = useMemo(() => t as any, [t, language]);
    const videoTranslations = useMemo(() => translation.VIDEO, [translation, language]);
    const buttonsTranslations = useMemo(() => translation.BUTTONS, [translation, language]);

    // Load about video from Firestore
    useEffect(() => {
        const loadAboutVideo = async () => {
            try {
                let videosSnap;
                let allVideos: any[] = [];
                
                try {
                    const aboutQuery = query(
                        getClientCollection('videos'),
                        where('type', '==', 'about')
                    );
                    videosSnap = await getDocs(aboutQuery);
                    
                    if (videosSnap.docs.length > 0) {
                        allVideos = videosSnap.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data()
                        }));
                    }
                } catch (whereError) {
                    try {
                        const allVideosQuery = query(getClientCollection('videos'));
                        videosSnap = await getDocs(allVideosQuery);
                        
                        allVideos = videosSnap.docs.map(doc => {
                            const data = doc.data();
                            return {
                                id: doc.id,
                                ...data
                            };
                        });
                    } catch (allError) {
                        return;
                    }
                }
                
                let aboutVideo = allVideos.find((video: any) => {
                    return video.type === 'about';
                });
                
                if (aboutVideo) {
                    const url = (aboutVideo as any).url || (aboutVideo as any).videoUrl || (aboutVideo as any).downloadURL;
                    if (url) {
                        setVideoUrl(url);
                        if (videoLinkRef.current) {
                            videoLinkRef.current.setAttribute('href', url);
                            videoLinkRef.current.setAttribute('data-video-url', url);
                            videoLinkRef.current.setAttribute('data-video-source', 'firebase');
                        }
                    }
                }
            } catch (_) {
            }
        };

        loadAboutVideo();
    }, []);

    useEffect(() => {
        if (!containerRef.current || !bgRef.current) {
            return;
        }

        let isMounted = true;

        // Try public path first, fallback to imported image if public path fails
        const imagePath = WHY_CHOOSE_US_IMAGE_PATH;
        const fallbackImagePath = whyChooseUsImageSrc;

        // Set static background image (no parallax)
        if (bgRef.current) {
            bgRef.current.style.backgroundImage = `url(${imagePath})`;
            bgRef.current.style.backgroundSize = 'cover';
            bgRef.current.style.backgroundPosition = 'center';
            bgRef.current.style.backgroundRepeat = 'no-repeat';
        }

        // Ensure background image is loaded
        const img = new Image();
        img.src = imagePath;
        
        img.onerror = () => {
            // If public path fails, try the imported/processed path
            if (bgRef.current && isMounted) {
                bgRef.current.style.backgroundImage = `url(${fallbackImagePath})`;
            }
        };

        // Initialize video popup with nice centered dialog
        // Add a small delay to ensure attributes are set first
        const timer = setTimeout(() => {
            if (isMounted && containerRef.current) {
                initializeVideoSectionPopup(containerRef.current);
            }
        }, 100);

        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [videoUrl]);

    return (
        <section className="video-one" ref={containerRef}>
            <div 
                className="video-one__bg" 
                style={{ backgroundImage: `url(${WHY_CHOOSE_US_IMAGE_PATH})` }}
                ref={bgRef}
            >
                <div className="video-one__inner">
                    <div className="video-one__video-link">
                        <a 
                            href={videoUrl || "https://www.youtube.com/watch?v=CR9Y3Do-aX8"} 
                            className="video-popup"
                            ref={videoLinkRef}
                        >
                            <div className="video-one__video-icon">
                                <span className="fas fa-play"></span>
                                <i className="ripple"></i>
                            </div>
                        </a>
                    </div>
                    <h3 className="video-one__title">
                        {videoTranslations?.professionalHandsExpertTools ?? 'Experiência, precisão e compromisso com o seu automóvel.'}
                    </h3>
                    <div className="video-one__btn-box">
                        <div className="video-one__shape-1">
                            <img loading="lazy" src={videoShape} alt="" />
                        </div>
                        <Link to={ROUTES.CONTACT} className="thm-btn view-more-btn">
                            {buttonsTranslations?.contactUs ?? 'Contact Us'}<span><i className="icon-next"></i></span>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VideoSection;

